import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw, FiHome, FiMap, FiStar, FiPlay } = FiIcons;

function NumberJumpsGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [selectedTable, setSelectedTable] = useState(null); // Which table to practice
  const [gameMode, setGameMode] = useState('selection'); // 'selection', 'playing', 'completed'
  const [currentStep, setCurrentStep] = useState(1); // 1x2, 2x2, etc.
  const [userAnswer, setUserAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [tableCompleted, setTableCompleted] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [jumpingNumbers, setJumpingNumbers] = useState([]);
  const [sessionStats, setSessionStats] = useState({
    correctAnswers: 0,
    totalAnswers: 0,
    timeSpent: 0
  });

  const maxTable = Math.min(state.age + 5, 12); // More generous range
  const stepsPerTable = Math.min(state.age + 3, 12); // More steps for practice

  // Available multiplication tables with fun themes
  const availableTables = [
    { number: 2, name: 'Jumping by 2s', emoji: '🐸', color: 'from-green-400 to-green-500', description: 'Hop like a frog!' },
    { number: 3, name: 'Jumping by 3s', emoji: '🦘', color: 'from-blue-400 to-blue-500', description: 'Bounce like a kangaroo!' },
    { number: 4, name: 'Jumping by 4s', emoji: '🐰', color: 'from-purple-400 to-purple-500', description: 'Leap like a bunny!' },
    { number: 5, name: 'Jumping by 5s', emoji: '🦌', color: 'from-orange-400 to-orange-500', description: 'Sprint like a deer!' },
    { number: 6, name: 'Jumping by 6s', emoji: '🐎', color: 'from-yellow-400 to-yellow-500', description: 'Gallop like a horse!' },
    { number: 7, name: 'Jumping by 7s', emoji: '🦓', color: 'from-pink-400 to-pink-500', description: 'Run like a zebra!' },
    { number: 8, name: 'Jumping by 8s', emoji: '🦒', color: 'from-indigo-400 to-indigo-500', description: 'Stretch like a giraffe!' },
    { number: 9, name: 'Jumping by 9s', emoji: '🐅', color: 'from-red-400 to-red-500', description: 'Pounce like a tiger!' },
    { number: 10, name: 'Jumping by 10s', emoji: '🦁', color: 'from-amber-400 to-amber-500', description: 'Roar like a lion!' },
    { number: 11, name: 'Jumping by 11s', emoji: '🐺', color: 'from-gray-400 to-gray-500', description: 'Hunt like a wolf!' },
    { number: 12, name: 'Jumping by 12s', emoji: '🐉', color: 'from-emerald-400 to-emerald-500', description: 'Soar like a dragon!' }
  ].filter(table => table.number <= maxTable);

  useEffect(() => {
    if (selectedTable && gameMode === 'playing') {
      // Generate jumping number sequence for current table
      const sequence = [];
      for (let i = 1; i <= stepsPerTable; i++) {
        sequence.push({
          step: i,
          multiplier: selectedTable,
          result: i * selectedTable,
          completed: completedSteps.includes(i)
        });
      }
      setJumpingNumbers(sequence);
    }
  }, [selectedTable, completedSteps, stepsPerTable, gameMode]);

  const handleTableSelection = (tableNumber) => {
    setSelectedTable(tableNumber);
    setGameMode('playing');
    setCurrentStep(1);
    setCompletedSteps([]);
    setTableCompleted(false);
    setUserAnswer('');
    setShowResult(false);
    setShowCelebration(false);
    setSessionStats({ correctAnswers: 0, totalAnswers: 0, timeSpent: 0 });
  };

  const handleAnswerSubmit = (e) => {
    e.preventDefault();
    const correctAnswer = currentStep * selectedTable;
    const userNum = parseInt(userAnswer);
    const correct = userNum === correctAnswer;
    
    setIsCorrect(correct);
    setShowResult(true);

    // Update session stats
    setSessionStats(prev => ({
      ...prev,
      totalAnswers: prev.totalAnswers + 1,
      correctAnswers: prev.correctAnswers + (correct ? 1 : 0)
    }));

    if (correct) {
      // Add to completed steps
      const newCompleted = [...completedSteps, currentStep];
      setCompletedSteps(newCompleted);
      
      // Award points and tokens
      const points = 10 + (selectedTable * 2); // More points for harder tables
      dispatch({ type: 'ADD_SCORE', payload: points });
      
      // Check if table is completed
      if (newCompleted.length >= stepsPerTable) {
        setTableCompleted(true);
        setShowCelebration(true);
        
        // Bonus tokens for completing a table
        const bonusTokens = selectedTable * 3;
        const accuracyBonus = Math.round((sessionStats.correctAnswers + 1) / (sessionStats.totalAnswers + 1) * 10);
        dispatch({ 
          type: 'GAME_COMPLETED', 
          payload: { 
            questId: `number-jumps-${selectedTable}`,
            bonusTokens: bonusTokens + accuracyBonus
          }
        });
        
        setTimeout(() => {
          setShowCelebration(false);
          setGameMode('completed');
        }, 3000);
      }
    } else {
      dispatch({ type: 'ADD_WRONG_ANSWER' });
    }

    setTimeout(() => {
      setShowResult(false);
      setUserAnswer('');
      
      if (correct && !tableCompleted) {
        // Move to next step
        if (currentStep < stepsPerTable) {
          setCurrentStep(currentStep + 1);
        }
      }
    }, 2000);
  };

  const resetToSelection = () => {
    setSelectedTable(null);
    setGameMode('selection');
    setCurrentStep(1);
    setCompletedSteps([]);
    setTableCompleted(false);
    setUserAnswer('');
    setShowResult(false);
    setShowCelebration(false);
    setSessionStats({ correctAnswers: 0, totalAnswers: 0, timeSpent: 0 });
  };

  const restartCurrentTable = () => {
    setCurrentStep(1);
    setCompletedSteps([]);
    setTableCompleted(false);
    setUserAnswer('');
    setShowResult(false);
    setGameMode('playing');
    setSessionStats({ correctAnswers: 0, totalAnswers: 0, timeSpent: 0 });
  };

  const currentTableInfo = availableTables.find(table => table.number === selectedTable);

  // Table Selection Screen
  if (gameMode === 'selection') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen p-4 pb-24"
      >
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="flex items-center justify-between mb-6"
          >
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
              >
                <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
                <span className="font-comic text-jungle-700">Back</span>
              </button>
              
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
              >
                <SafeIcon icon={FiHome} className="text-jungle-600" />
                <span className="font-comic text-jungle-700">Home</span>
              </button>
            </div>

            <div className="text-center">
              <h1 className="text-3xl font-kid text-jungle-700">Number Jumps 🐸</h1>
              <p className="font-comic text-jungle-600">Choose Your Adventure!</p>
            </div>

            <div className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200">
              <span className="text-xl">🪙</span>
              <span className="font-kid text-jungle-700 text-lg">{state.tokens}</span>
            </div>
          </motion.div>

          {/* Welcome Message */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 mb-8 text-center"
          >
            <div className="text-6xl mb-4">🎯</div>
            <h2 className="text-2xl font-kid text-jungle-700 mb-4">
              Welcome to Number Jumps, {state.playerName}!
            </h2>
            <p className="font-comic text-jungle-600 text-lg mb-4">
              Choose which multiplication table you want to practice. Each table has its own fun animal friend!
            </p>
            <div className="bg-jungle-100 rounded-2xl p-4 inline-block">
              <p className="font-comic text-jungle-700">
                💡 <strong>Tip:</strong> Start with smaller numbers like 2s and 3s, then work your way up!
              </p>
            </div>
          </motion.div>

          {/* Table Selection Grid */}
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-xl font-kid text-jungle-700 mb-6 text-center">
              Pick Your Multiplication Table:
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {availableTables.map((table, index) => (
                <motion.button
                  key={table.number}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleTableSelection(table.number)}
                  className={`bg-gradient-to-br ${table.color} rounded-3xl p-6 shadow-xl cursor-pointer text-white transform transition-all duration-200 hover:shadow-2xl relative overflow-hidden`}
                >
                  <div className="text-center relative z-10">
                    <div className="text-5xl mb-3">{table.emoji}</div>
                    <h4 className="text-lg font-kid mb-2">{table.name}</h4>
                    <p className="font-comic text-sm opacity-90 mb-3">{table.description}</p>
                    
                    {/* Difficulty indicator */}
                    <div className="flex justify-center mb-2">
                      {Array.from({ length: Math.min(table.number, 5) }, (_, i) => (
                        <span key={i} className="text-yellow-300 text-sm">⭐</span>
                      ))}
                    </div>
                    
                    <div className="bg-white bg-opacity-20 rounded-full px-3 py-1 inline-flex items-center">
                      <SafeIcon icon={FiPlay} className="text-sm mr-1" />
                      <span className="text-xs font-comic">Start Jumping!</span>
                    </div>
                  </div>

                  {/* Decorative background */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-2 right-2 text-2xl transform rotate-12">{table.emoji}</div>
                    <div className="absolute bottom-2 left-2 text-xl transform -rotate-12">{table.emoji}</div>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Quick Tips */}
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 bg-gradient-to-r from-blue-100 to-purple-100 rounded-3xl p-6 border-4 border-blue-200"
          >
            <div className="text-center">
              <div className="text-4xl mb-3">🎓</div>
              <h3 className="text-xl font-kid text-jungle-700 mb-4">Learning Tips</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm font-comic text-jungle-600">
                <div className="bg-white rounded-2xl p-4">
                  <div className="text-2xl mb-2">🥇</div>
                  <p><strong>Start Easy:</strong> Begin with 2s, 5s, and 10s - they have easy patterns!</p>
                </div>
                <div className="bg-white rounded-2xl p-4">
                  <div className="text-2xl mb-2">🧠</div>
                  <p><strong>Practice:</strong> The more you practice, the faster you'll remember!</p>
                </div>
                <div className="bg-white rounded-2xl p-4">
                  <div className="text-2xl mb-2">🎉</div>
                  <p><strong>Have Fun:</strong> Each animal friend will help you learn their table!</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Game Completed Screen
  if (gameMode === 'completed') {
    const accuracy = Math.round((sessionStats.correctAnswers / sessionStats.totalAnswers) * 100);
    
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4 pb-24"
      >
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-safari-200 text-center max-w-md">
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
            className="text-8xl mb-4"
          >
            {currentTableInfo?.emoji}
          </motion.div>
          <h2 className="text-3xl font-kid text-jungle-700 mb-4">
            Amazing! You mastered {selectedTable}× table!
          </h2>
          <div className="bg-jungle-100 rounded-2xl p-4 mb-6">
            <p className="font-comic text-jungle-600 mb-2">
              <strong>Your Results:</strong>
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-2xl">✅</div>
                <div className="font-kid text-jungle-700">{sessionStats.correctAnswers}/{sessionStats.totalAnswers}</div>
                <div className="font-comic text-jungle-600">Correct</div>
              </div>
              <div>
                <div className="text-2xl">🎯</div>
                <div className="font-kid text-jungle-700">{accuracy}%</div>
                <div className="font-comic text-jungle-600">Accuracy</div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={restartCurrentTable}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Practice {selectedTable}× Again
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetToSelection}
              className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Choose Different Table
            </motion.button>
            <div className="grid grid-cols-2 gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-safari-500 to-safari-600 text-white p-3 rounded-2xl font-kid text-sm shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiHome} className="text-lg" />
                <span>Home</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/quest-map')}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-2xl font-kid text-sm shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiMap} className="text-lg" />
                <span>Quest Map</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Playing Game Screen
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4 pb-24"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-6"
        >
          <div className="flex items-center space-x-3">
            <button
              onClick={resetToSelection}
              className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
            >
              <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
              <span className="font-comic text-jungle-700">Choose Table</span>
            </button>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
            >
              <SafeIcon icon={FiHome} className="text-jungle-600" />
              <span className="font-comic text-jungle-700">Home</span>
            </button>
          </div>

          <div className="text-center">
            <h1 className="text-3xl font-kid text-jungle-700">
              {currentTableInfo?.emoji} {selectedTable}× Table
            </h1>
            <p className="font-comic text-jungle-600">{currentTableInfo?.description}</p>
          </div>

          <button
            onClick={restartCurrentTable}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Restart</span>
          </button>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(completedSteps.length / stepsPerTable) * 100}%` }}
          className="h-4 bg-green-400 rounded-full mb-6 shadow-inner relative"
        >
          <div className="w-full h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-end pr-2">
            <span className="text-white font-comic text-xs">
              {completedSteps.length}/{stepsPerTable}
            </span>
          </div>
        </motion.div>

        {/* Number Jump Path */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`bg-gradient-to-br ${currentTableInfo?.color} rounded-3xl p-6 shadow-xl border-4 border-white mb-6 text-white`}
        >
          <h2 className="text-2xl font-kid text-center mb-6">
            Jump by {selectedTable}s with {currentTableInfo?.emoji}!
          </h2>

          {/* Number Line */}
          <div className="relative mb-8">
            <div className="flex justify-between items-center bg-white bg-opacity-20 rounded-2xl p-4 overflow-x-auto">
              {jumpingNumbers.map((num, index) => (
                <motion.div
                  key={num.step}
                  initial={{ scale: 0, y: -20 }}
                  animate={{ 
                    scale: num.completed ? 1.2 : 1,
                    y: num.completed ? -10 : 0
                  }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex flex-col items-center min-w-16 ${
                    num.step === currentStep ? 'z-10' : ''
                  }`}
                >
                  {/* Jumping Animal */}
                  {num.step === currentStep && (
                    <motion.div
                      animate={{ 
                        y: [-5, -25, -5],
                        rotate: [0, -10, 10, 0]
                      }}
                      transition={{ 
                        duration: 1,
                        repeat: Infinity,
                        repeatType: "reverse"
                      }}
                      className="text-4xl mb-2"
                    >
                      {currentTableInfo?.emoji}
                    </motion.div>
                  )}
                  
                  {/* Number Circle */}
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-kid text-lg border-4 transition-all duration-200 ${
                      num.completed 
                        ? 'bg-green-400 text-white border-green-500 shadow-lg' 
                        : num.step === currentStep
                        ? 'bg-yellow-400 text-jungle-700 border-yellow-500 shadow-lg ring-4 ring-yellow-200'
                        : 'bg-white bg-opacity-50 text-white border-white border-opacity-50'
                    }`}
                  >
                    {num.completed ? '✓' : num.result}
                  </motion.div>
                  
                  {/* Step Label */}
                  <div className="text-xs font-comic text-white mt-1">
                    {num.step}×{selectedTable}
                  </div>
                  
                  {/* Completion Star */}
                  {num.completed && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-2 -right-2"
                    >
                      <SafeIcon icon={FiStar} className="text-yellow-400 text-xl" />
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Jump Trail */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-white bg-opacity-30 rounded-full transform -translate-y-1/2 -z-10"></div>
          </div>

          {/* Current Question */}
          {!tableCompleted && (
            <motion.div
              key={currentStep}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center mb-6"
            >
              <div className="text-6xl mb-4 text-white">
                {currentStep} × {selectedTable} = ?
              </div>
              
              <p className="font-comic text-white text-lg mb-4">
                Jump {currentStep} times by {selectedTable}!
              </p>

              {/* Visual Representation */}
              <div className="flex justify-center items-center space-x-2 mb-6 flex-wrap">
                {Array.from({ length: currentStep }, (_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: i * 0.2 }}
                    className="flex flex-col items-center"
                  >
                    <div className="text-2xl mb-1">{currentTableInfo?.emoji}</div>
                    <div className="bg-white bg-opacity-20 rounded-lg px-2 py-1 font-comic text-sm text-white">
                      +{selectedTable}
                    </div>
                  </motion.div>
                ))}
                <div className="text-4xl mx-4 text-white">=</div>
                <div className="text-4xl text-yellow-300">?</div>
              </div>

              {/* Answer Input */}
              <form onSubmit={handleAnswerSubmit} className="flex justify-center">
                <div className="flex items-center space-x-4">
                  <input
                    type="number"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    placeholder="Your answer"
                    className="w-32 p-4 text-2xl font-kid text-center border-4 border-white rounded-2xl focus:outline-none focus:border-yellow-400 focus:ring-4 focus:ring-yellow-200 text-jungle-700"
                    disabled={showResult}
                    autoFocus
                  />
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={!userAnswer || showResult}
                    className="bg-white text-jungle-700 px-6 py-4 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Jump!
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Result Message */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center"
              >
                {isCorrect ? (
                  <div className="text-yellow-300 font-kid text-2xl">
                    🎉 Perfect Jump! {currentStep} × {selectedTable} = {currentStep * selectedTable}
                  </div>
                ) : (
                  <div className="text-red-300 font-kid text-2xl">
                    Oops! {currentStep} × {selectedTable} = {currentStep * selectedTable}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Table Completion */}
          {tableCompleted && !showCelebration && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center"
            >
              <div className="text-6xl mb-4">{currentTableInfo?.emoji}</div>
              <h3 className="text-2xl font-kid text-white mb-4">
                Amazing! You mastered the {selectedTable}× table!
              </h3>
              <p className="font-comic text-white mb-6">
                You earned {selectedTable * 3} tokens plus accuracy bonus! 🪙
              </p>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setGameMode('completed')}
                className="bg-white text-jungle-700 px-8 py-4 rounded-2xl font-kid text-xl shadow-lg hover:shadow-xl transition-all duration-200"
              >
                See Results! 🏆
              </motion.button>
            </motion.div>
          )}
        </motion.div>

        {/* Session Stats */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-3xl p-6 shadow-xl border-4 border-safari-200"
        >
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl mb-1">📊</div>
              <div className="font-kid text-jungle-700 text-lg">{sessionStats.correctAnswers}/{sessionStats.totalAnswers}</div>
              <div className="font-comic text-jungle-600 text-sm">Correct</div>
            </div>
            <div>
              <div className="text-2xl mb-1">🎯</div>
              <div className="font-kid text-jungle-700 text-lg">
                {sessionStats.totalAnswers > 0 ? Math.round((sessionStats.correctAnswers / sessionStats.totalAnswers) * 100) : 0}%
              </div>
              <div className="font-comic text-jungle-600 text-sm">Accuracy</div>
            </div>
            <div>
              <div className="text-2xl mb-1">⭐</div>
              <div className="font-kid text-jungle-700 text-lg">{completedSteps.length}</div>
              <div className="font-comic text-jungle-600 text-sm">Completed</div>
            </div>
          </div>
        </motion.div>

        {/* Celebration Overlay */}
        <AnimatePresence>
          {showCelebration && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 180 }}
                className="bg-white rounded-3xl p-8 text-center max-w-md mx-4"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, -10, 0]
                  }}
                  transition={{ 
                    duration: 1,
                    repeat: Infinity
                  }}
                  className="text-8xl mb-4"
                >
                  {currentTableInfo?.emoji}
                </motion.div>
                <h2 className="text-3xl font-kid text-jungle-700 mb-4">
                  Table {selectedTable} Mastered!
                </h2>
                <p className="font-comic text-jungle-600 text-lg">
                  You're becoming a multiplication master! 🌟
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default NumberJumpsGame;